<template>
  <div class="tab-grid-wrapper">
    <!-- Toolbar -->
    <div class="filter-row">
      <div class="filter-scroll">
        <div v-for="(f, fi) in docsView.filters" :key="'dvf' + fi" class="filter-col">
          <div class="filter-label">{{ f.label }}</div>
          <RmSelect
            v-model="f.value"
            :items="f.items"
            multiple clearable
            :placeholder="f.placeholder"
            placeholder-color="#000000"
            class="filter-select"
          />
        </div>
      </div>
      <div class="filter-actions">
        <div class="settings-anchor">
          <button class="action-button-small" @click="toggleSettings" title="Settings">
            <v-icon small>mdi-cog</v-icon>
          </button>
          <div v-if="settingsOpen" class="settings-backdrop" @click="settingsOpen = false"></div>
          <div v-if="settingsOpen" class="settings-menu" :style="settingsMenuStyle">
            <div class="settings-menu-header">
              <span class="settings-menu-title">Settings</span>
              <v-icon small @click="settingsOpen = false" style="cursor: pointer;">mdi-close</v-icon>
            </div>
            <div class="settings-menu-body">
              <div class="settings-field">
                <div class="filter-label">Columns</div>
                <RmSelect
                  :items="docsView.columnsAvailable"
                  :value="docsView.columnProperty.target"
                  itemText="label" itemValue="target"
                  @input="setDocAxis('columnProperty', $event)"
                  class="filter-select"
                />
              </div>
              <div class="settings-field">
                <div class="filter-label">Rows</div>
                <RmSelect
                  :items="docsView.columnsAvailable"
                  :value="docsView.rowProperty.target"
                  itemText="label" itemValue="target"
                  @input="setDocAxis('rowProperty', $event)"
                  class="filter-select"
                />
              </div>
              <div class="settings-field">
                <div class="filter-label">Group rows by</div>
                <RmSelect
                  :items="[ emptyColumn, ...docsView.columnsAvailable.filter(k => k.target !== docsView.columnProperty.target && k.target !== docsView.rowProperty.target)]"
                  :value="docsView.rowGroupProperty.target"
                  itemText="label" itemValue="target"
                  @input="setDocAxis('rowGroupProperty', $event)"
                  class="filter-select"
                />
              </div>
              <div class="settings-field">
                <label class="filter-label">
                  <input type="checkbox" v-model="docsView.showInvalid" /> Show Invalid Documents
                </label>
              </div>
              <div class="settings-field">
                <label class="filter-label">
                  <input type="checkbox" v-model="docsView.showEmptyColumns" /> Show Empty Columns
                </label>
              </div>
              <div class="settings-field">
                <label class="filter-label">
                  <input type="checkbox" v-model="docsView.showEmptyRows" /> Show Empty Rows
                </label>
              </div>
            </div>
          </div>
        </div>
        <button class="action-button-small" @click="fetchDocumentGrid" title="Refresh">
          <v-icon small>mdi-refresh</v-icon>
        </button>
      </div>
    </div>

    <!-- Grid -->
    <div v-if="docsView.loading" style="text-align: center; padding: 48px;">
      <div class="loader"></div>
      <span>Loading documents...</span>
    </div>
    <div v-else-if="!docsView.documents.length" style="padding: 24px; color: #888; text-align: center;">
      No documents found. The document data pipeline is not yet connected.
    </div>
    <div v-else class="framework-grid-wrapper">
      <div class="framework-grid">
        <table class="grid-table doc-grid-table">
          <thead>
            <tr class="header-row">
              <th class="header-cell level-header">
                <span v-if="docsView.rowGroupProperty.target !== emptyColumn.target">{{ docsView.rowGroupProperty.label }} / </span>{{ docsView.rowProperty.label }}
              </th>
              <th v-for="col in docColumns" :key="col.key" class="header-cell track-header"
                  :style="docsView.columnProperty.target === 'LF_TRACK' && col.background_colour ? { backgroundColor: col.background_colour } : {}">
                {{ col.label }}
                <v-icon v-if="!col.id && col.is_lft" small color="amber" :title="'Invalid ' + docsView.columnProperty.label">mdi-alert</v-icon>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="group in docRowGroups">
              <!-- Group header row -->
              <tr v-if="docsView.rowGroupProperty.target !== emptyColumn.target" :key="'g-' + group.key"
                  class="doc-grid-group-row" @click="toggleDocGroup(group.key)">
                <td>
                  <v-icon small class="mr-1">{{ docsView.expandedGroups[group.key] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                  <strong>{{ group.label }}</strong>
                  <span class="ml-2 doc-grid-group-count">{{ group.docCount }}</span>
                </td>
                <td v-for="col in docColumns" :key="'gs-' + col.key" style="text-align: center;">
                  <div class="doc-grid-group-count"
                    v-for="(v, vi) in (group.columnCounts[col.key] || [])"
                    :key="'gs-' + col.key + 'v' + vi"
                    :style="v.background_colour ? 'background-color: ' + v.background_colour : ''">
                    {{ v.count }}
                  </div>
                </td>
              </tr>
              <!-- Detail rows -->
              <template v-if="docsView.rowGroupProperty.target === emptyColumn.target || docsView.expandedGroups[group.key]">
                <tr v-for="row in group.rows" :key="group.key + '-' + row.key"
                    class="doc-grid-row">
                  <td class="doc-grid-row-label"
                    :style="row.background_colour ? { backgroundColor: row.background_colour } : {}">{{ row.label }}
                    <v-icon v-if="!row.id && row.is_lft" small color="amber" :title="'Invalid ' + docsView.rowProperty.label">mdi-alert</v-icon>
                  </td>
                  <td v-for="col in docColumns" :key="col.key" class="doc-grid-cell">
                    <div v-for="doc in (row.cells[col.key] || [])" :key="doc.doc_id"
                      class="doc-grid-doc" :title="doc.reference"
                      :style="row.background_colour ? { backgroundColor: row.background_colour } : {}"
                      @click="openDocPopup(doc, $event)">
                      <div class="doc-grid-text" :title="doc.doc_name">{{ doc.doc_name }}</div>
                      <div v-if="doc.lft_abbreviation" class="doc-grid-lft">{{ doc.lft_abbreviation }}</div>
                      <div v-else class="doc-grid-issue" :title="doc.lft_status"><v-icon small :color="doc.lf_level_id_valid && doc.lf_function_id_valid && doc.lf_track_id_valid ? 'red' : 'amber'">mdi-alert</v-icon></div>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <DocLevellingPopUp
      :show="docPopup.show"
      :details="docPopup.details"
      :attributes="docsView.columnsAvailable"
      @close="docPopup.show = false"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, set, watch, toRef } from 'vue';
import RmSelect from '@/components/common/RmSelect.vue';
import DocLevellingPopUp from '@/components/levelling/DocLevellingPopUp.vue';
import axios from "axios";
import utils from "@/common/utils.js";

const emptyColumn = { target: 'none', label: 'None' };

export default {
  name: "DocumentsView",
  components: { RmSelect, DocLevellingPopUp },
  props: {
    levellingFramework: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const settingsOpen = ref(false);
    const settingsMenuPos = reactive({ top: 0, right: 0 });

    const toggleSettings = (event) => {
      if (settingsOpen.value) {
        settingsOpen.value = false;
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      settingsMenuPos.top = rect.bottom + 4;
      settingsMenuPos.right = window.innerWidth - rect.right;
      settingsOpen.value = true;
    };

    const settingsMenuStyle = computed(() => ({
      top: settingsMenuPos.top + 'px',
      right: settingsMenuPos.right + 'px',
    }));

    const docsView = reactive({
      loading: false,
      documents: [],
      columnProperty: emptyColumn,
      rowProperty: emptyColumn,
      rowGroupProperty: emptyColumn,
      groups: {},
      expandedGroups: {},
      filters: [],
      showInvalid: false,
      showEmptyRows: false,
      showEmptyColumns: true,
      columnsAvailable: [],
      framework: {},
    });

    const docAxisValues = (column, axis) => {
      if (!column.valueList) return [];
      const valueList = docsView.showInvalid ? column.valueList : column.valueList.filter(v => v.id || !column.is_lft);
      const vals = valueList.map(v => ({ key: v.value, label: v.value, position: v.position, background_colour: v.background_colour, text_colour: v.text_colour }));
      if (docsView.filters.some(x => x.value)
          || (axis === "columns" && !docsView.showEmptyColumns)
          || (axis === "rows" && !docsView.showEmptyRows)) {
        const docs = docFilteredDocuments.value;
        return vals.filter(v => docs.some(d => d[column.target] === v.key));
      }
      return vals;
    };

    const docFilteredDocuments = computed(() => {
      let docs = docsView.documents;
      const filters = docsView.filters.filter(x => x.value?.length);
      if (filters.length)
        docs = docs.filter(d => filters.every(f => f.value.includes(d[f.target])));
      return docsView.showInvalid ? docs : docs.filter(d => d.lft_abbreviation);
    });

    const docApplyGroups = (key, values) => {
      const groupDefs = docsView.groups[key];
      if (!groupDefs || !groupDefs.length) return values;
      const grouped = [];
      const assigned = new Set();
      groupDefs.forEach(g => {
        grouped.push({ key: 'group:' + g.name, label: g.name, values: g.values, isGroup: true });
        g.values.forEach(v => assigned.add(v));
      });
      values.forEach(v => {
        if (!assigned.has(v.key)) grouped.push(v);
      });
      return grouped;
    };

    const docColumns = computed(() => {
      const raw = docAxisValues(docsView.columnProperty, "columns");
      return docApplyGroups(docsView.columnProperty, raw);
    });

    const docRowGroups = computed(() => {
      const rowVals = docApplyGroups(docsView.rowProperty, docAxisValues(docsView.rowProperty, "rows"));
      const cols = docColumns.value;
      const docs = docFilteredDocuments.value;
      const colField = docsView.columnProperty.target;
      const rowField = docsView.rowProperty.target;
      const hasGrouping = docsView.rowGroupProperty.target !== emptyColumn.target;

      const buildRows = (groupDocs) => rowVals.map(row => {
        const rowMatch = row.isGroup ? row.values : [row.key];
        const cells = {};
        row.hasDocs = false;
        for (const col of cols) {
          const colMatch = col.isGroup ? col.values : [col.key];
          cells[col.key] = groupDocs.filter(d =>
            rowMatch.includes(d[rowField]) && colMatch.includes(d[colField])
          );
          if (cells[col.key].length) row.hasDocs = true;
        }
        return { ...row, cells };
      });

      const buildColumnCounts = (groupKey, groupDocs) => {
        if (docsView.expandedGroups[groupKey]) return {};
        const counts = {};
        for (const col of cols) {
          const colMatch = col.isGroup ? col.values : [col.key];
          const filtered = groupDocs.filter(d => colMatch.includes(d[colField]));
          let vals = [];
          if (groupKey !== '__all' && rowVals.length) {
            for (const r of rowVals) {
              vals.push({ count: filtered.filter(d => [r.key].includes(d[rowField])).length, text: r.label, background_colour: r.background_colour });
            }
          }
          if (!vals.length) vals.push({ count: filtered.length });
          counts[col.key] = vals.filter(v => v.count > 0);
        }
        return counts;
      };

      if (!hasGrouping) {
        return [{ key: '__all', label: '', rows: buildRows(docs), expanded: true, docCount: docs.length, columnCounts: {} }];
      }

      const groupVals = docApplyGroups(docsView.rowGroupProperty, docAxisValues(docsView.rowGroupProperty, "rows"));
      const groupField = docsView.rowGroupProperty.target;
      const drg =  groupVals.map(g => {
        const matchValues = g.isGroup ? g.values : [g.key];
        const groupDocs = docs.filter(d => matchValues.includes(d[groupField]));
        const rows = buildRows(groupDocs);
        return {
          key: g.key, label: g.label, docCount: groupDocs.length,
          rows: docsView.showEmptyRows ? rows : rows.filter(r => r.hasDocs),
          columnCounts: buildColumnCounts(g.key, groupDocs),
        };
      });
      return drg;
    });

    const fetchDocumentGrid = () => {
      docsView.loading = true;
      axios.get(`document/getLevellingFrameworkDocuments/${props.levellingFramework?.levelling_framework_id}`).then(resp => {
        if (resp.data.Status === 'OK' && resp.data.Data) {
          docsView.documents = resp.data.Data.documents || [];
          docsView.framework = resp.data.Data.levelling_framework;
          docsView.filters = [];
          docsView.columnProperty = null;
          docsView.rowProperty = null;
          docsView.rowGroupProperty = null;
          docsView.columnsAvailable = resp.data.Data.levellingAttributes.filter(x => x.present);
          docsView.columnsAvailable.forEach(a => {
            docsView.filters.push({
              value: null,
              items: a.valueList.map(v => ({ text: v.value, value: v.value })),
              placeholder: `All ${utils.pluralise(a.label)}`,
              label: a.label,
              target: a.target,
            });
            if (a.name === "LEVEL_GROUP") docsView.columnProperty = a;
            if (a.name === "TRACK") docsView.rowProperty = a;
            if (a.name === "FAMILY") docsView.rowGroupProperty = a;
          });
          if (!docsView.columnProperty) {
            let avail = docsView.columnsAvailable.find(c => c.target !== docsView.columnProperty?.target && c.target !== docsView.rowProperty?.target && c.target !== docsView.rowGroupProperty?.target);
            if (avail) docsView.columnProperty = avail;
          }
          if (!docsView.rowProperty) {
            let avail = docsView.columnsAvailable.find(c => c.target !== docsView.columnProperty?.target && c.target !== docsView.rowProperty?.target && c.target !== docsView.rowGroupProperty?.target);
            if (avail) docsView.rowProperty = avail;
          }
          if (!docsView.rowGroupProperty) {
            let avail = docsView.columnsAvailable.find(c => c.target !== docsView.columnProperty?.target && c.target !== docsView.rowProperty?.target && c.target !== docsView.rowGroupProperty?.target);
            if (avail) docsView.rowGroupProperty = avail;
          }
        }
      }).finally(() => { docsView.loading = false; });
    };

    watch(toRef(props, 'levellingFramework'), () => {
      fetchDocumentGrid();
    }, { immediate: true });

    const toggleDocGroup = (key) => {
      set(docsView.expandedGroups, key, !docsView.expandedGroups[key]);
    };

    const setDocAxis = (axis, target) => {
      const prev = docsView[axis];
      if (axis === 'columnProperty') {
        if (target === docsView.rowProperty.target) docsView.rowProperty = prev;
        else if (target === docsView.rowGroupProperty.target) docsView.rowGroupProperty = prev;
      } else if (axis === 'rowProperty') {
        if (target === docsView.columnProperty.target) docsView.columnProperty = prev;
        else if (target === docsView.rowGroupProperty.target) docsView.rowGroupProperty = prev;
      } else if (axis === 'rowGroupProperty') {
        if (target !== emptyColumn.target) {
          if (target === docsView.columnProperty.target) docsView.columnProperty = prev === emptyColumn.target ? docsView.columnsAvailable.find(k => k.target !== target && k.target !== docsView.rowProperty.target) : prev;
          else if (target === docsView.rowProperty.target) docsView.rowProperty = prev === emptyColumn.target ? docsView.columnsAvailable.find(k => k.target !== target && k.target !== docsView.columnProperty.target) : prev;
        }
      }
      docsView[axis] = target === emptyColumn.target ? emptyColumn : docsView.columnsAvailable.find(x => x.target === target);
    };

    const docPopup = reactive({ show: false, details: [], posX: 0, posY: 0 });

    const openDocPopup = (doc, event) => {
      docPopup.posX = event.clientX;
      docPopup.posY = event.clientY;
      docPopup.show = true;
      const famDocs = docsView.documents.filter(d => d.LF_FAMILY !== "Unknown" && d.LF_FAMILY === doc.LF_FAMILY && d.doc_id !== doc.doc_id && !isNaN(d.LF_LEVEL) && !isNaN(doc.LF_LEVEL)).sort((a, b) => Number(a.LF_LEVEL) > Number(b.LF_LEVEL) ? 1 : Number(a.LF_LEVEL) < Number(b.LF_LEVEL) ? -1 : 0 );
      const previous = famDocs.filter(d => Number(d.LF_LEVEL) < Number(doc.LF_LEVEL));
      const next = famDocs.filter(d => Number(d.LF_LEVEL) > Number(doc.LF_LEVEL));
      const current = famDocs.filter(d => Number(d.LF_LEVEL) === Number(doc.LF_LEVEL));
      docPopup.details = [{
        document: doc,
        related: {
          previous: previous,
          next: next,
          current: current,
        }
      }];
    };

    return {
      settingsOpen,
      settingsMenuStyle,
      toggleSettings,
      emptyColumn,
      docsView,
      docColumns,
      docRowGroups,
      docFilteredDocuments,
      fetchDocumentGrid,
      toggleDocGroup,
      setDocAxis,
      docPopup,
      openDocPopup,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/vars";

.tab-grid-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.filter-row {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  border-radius: 8px;
  padding: 8px 0;
  margin: 0;
  gap: 8px;
}

.filter-scroll {
  display: flex;
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  gap: 8px;
  align-items: center;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
}

.filter-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-col {
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-family: 'DM Sans' !important;
  font-weight: 400;
  color: #000000 !important;
  letter-spacing: 0;
  line-height: 140%;
  white-space: nowrap;
}

.filter-select {
  min-width: 180px;
  max-width: 280px;
}

.framework-grid-wrapper {
  overflow: auto;
  flex: 1;
  min-height: 0;
  border-radius: 16px;
  border: 1px solid #DDDDDD
}

.framework-grid {
  min-width: max-content;
}

.grid-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: auto;
}

.header-row {
  background-color: white;

  th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: white;
  }
}

.header-cell {
  padding: 10px 12px;
  font-weight: 600;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
  border-bottom: 2px solid #e0e0e0;
}

.level-header {
  position: sticky;
  left: 0;
  z-index: 1;
  background: white;
}

.track-header {
  text-align: center;
  min-width: 120px;
}

.loader {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// --- Document Grid ---
.doc-grid-table {
  border-collapse: collapse;
  width: 100%;
}
.doc-grid-group-row {
  cursor: pointer;
  background: white;
  td {
    padding: 8px 12px;
    font-size: 14px;
    border-bottom: 1px solid #e0e0e0;
  }
  &:hover {
    background: #ebebeb;
  }
}
.doc-grid-group-count {
  display: inline-block;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  font-family: 'DM Sans' !important;
  letter-spacing: 0%;
  color: #000000 !important;
  border: 1px lightgray solid;
  border-radius: 4px;
  padding: 0 4px;
  margin: 0 4px;
  background-color: #DDDDDD;
}
.doc-grid-row {
  transition: background-color 0.15s;
}
.doc-grid-row-label {
  padding: 8px 12px 6px 40px;
  font-weight: 700;
  font-size: 14px;
  font-family: 'DM Sans' !important;
  letter-spacing: 0%;
  white-space: nowrap;
  color: #000000 !important;
  // border-right: 1px solid #e0e0e0;
  // border-bottom: 1px solid #f0f0f0;
}
.doc-grid-cell {
  padding: 4px 6px;
  vertical-align: top;
  // border-bottom: 1px solid #f0f0f0;
  // border-right: 1px solid #f0f0f0;
  min-width: 120px;
  max-width: 180px;
  max-height: 140px;
  overflow-y: auto;
}
.doc-grid-doc {
  padding: 2px 8px;
  margin: 4px;
  font-size: 12px;
  background: #e8eaf6;
  border-radius: 8px;
  width: calc(100% - 8px);
  height: 26px;
  cursor: pointer;
}
.doc-grid-text {
  display: inline-block;
  font-size: 12px;
  color: black;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: calc(100% - 30px);
}
.doc-grid-lft, .doc-grid-issue {
  padding: 2px 4px 0;
  margin-top: 2px;
  font-size: 12px;
  line-height: 100%;
  color: black;
  border-radius: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 40px;
  border: 1px solid gray;
  float: right;
}
 .doc-grid-issue {
  border: none;
  padding-top: 0;
 }

.settings-anchor {
  position: relative;
  display: inline-flex;
}

.settings-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
}

.settings-menu {
  position: fixed;
  z-index: 100;
  background: #F7F9FB;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  width: 280px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.settings-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.settings-menu-title {
  font-size: 14px;
  font-family: 'DM Sans' !important;
  font-weight: 700;
  color: #000000 !important;
}

.settings-menu-body {
  padding: 12px 16px;
  overflow-y: auto;
}

.settings-field {
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
}

.action-button-small {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 13px;
  background: transparent;
  border: 1px solid #ccc;
  border-radius: 6px;
  cursor: pointer;
  color: #333;
  &:hover {
    background: #f0f0f0;
  }
}
</style>
